import React from "react";
import {
  CheckCircle2, ChevronRight, Sparkles, ArrowRight, Circle, FileText,
  AlertCircle, Send, Plus, Users, Scale, MoreHorizontal,
} from "lucide-react";
import { LIGHT, cardStyle, initialsOf, daysFromNow, fmtDate } from "./RoomFlow";

const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-[13.5px] font-bold tracking-tight" style={{ color: LIGHT.textPrimary }}>
      {children}
    </h3>
    {action}
  </div>
);

const SmallLink = ({ children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-[11.5px] font-semibold inline-flex items-center gap-1"
    style={{ color: "#6366F1" }}
  >
    {children} <ArrowRight size={11} />
  </button>
);

/* ─── Deal Health card (with bars) ──────────────────────────────── */
const Bar = ({ label, value, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-[11px] w-[110px] shrink-0" style={{ color: LIGHT.textMuted }}>{label}</span>
    <div className="flex-1 h-[6px] rounded-full" style={{ background: "#F1F5F9" }}>
      <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
    </div>
    <span className="text-[11px] font-bold tabular-nums w-[40px] text-right" style={{ color: LIGHT.textPrimary }}>{Math.round(value)}%</span>
  </div>
);

const HealthRingSmall = ({ value }) => {
  const v = Math.max(0, Math.min(100, value));
  const r = 32;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;
  const color = v >= 80 ? "#10B981" : v >= 60 ? "#22C55E" : v >= 40 ? "#F59E0B" : "#EF4444";
  return (
    <div className="relative w-[88px] h-[88px] mx-auto">
      <svg viewBox="0 0 88 88" className="w-full h-full -rotate-90">
        <circle cx="44" cy="44" r={r} stroke="#E2E8F0" strokeWidth="6" fill="none" />
        <circle cx="44" cy="44" r={r} stroke={color} strokeWidth="6" fill="none" strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[26px] font-bold tabular-nums" style={{ color: LIGHT.textPrimary }}>{v}</span>
      </div>
    </div>
  );
};

export const DealHealthCard = ({ deal }) => {
  const ch = deal.chainHealth ?? 90;
  const progressPct = (() => {
    const m = {
      lead: 4, qualified: 10, viewing: 18, offer: 26, aml: 34, memo: 40, solicitors: 46,
      searches: 54, mortgage_app: 60, survey: 66, enquiries: 72, mortgage_offer: 78,
      exchange_ready: 86, exchanged: 92, completed: 100,
    };
    return m[deal.stage] ?? 50;
  })();
  const dExch = daysFromNow(deal.targetExchange);
  const timePct = dExch === null ? 60 : Math.max(0, Math.min(100, dExch < 0 ? 30 : 30 + dExch * 1.5));
  const responseScore = Math.min(100, Math.max(40, ch + 8));
  const chainStrength = Math.min(100, Math.max(40, ch));
  const riskScore = Math.max(0, 100 - ch);

  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-deal-health">
      <SectionTitle action={<SmallLink testid="dealroom-deal-health-why">Why this score?</SmallLink>}>
        Deal Health
      </SectionTitle>
      <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-4 items-center">
        <HealthRingSmall value={ch} />
        <div className="space-y-2">
          <Bar label="Progress"      value={progressPct}  color="#6366F1" />
          <Bar label="Time"          value={timePct}      color="#3B82F6" />
          <Bar label="Risk"          value={riskScore}    color="#F59E0B" />
          <Bar label="Response"      value={responseScore} color="#10B981" />
          <Bar label="Chain Strength" value={chainStrength} color="#10B981" />
        </div>
      </div>
    </section>
  );
};

/* ─── Participants card ─────────────────────────────────────────── */
const presence = (iso) => {
  if (!iso) return { tone: "#94A3B8", label: "Offline" };
  const m = (Date.now() - Date.parse(iso)) / 60000;
  if (m < 5)  return { tone: "#10B981", label: "Online" };
  if (m < 60) return { tone: "#F59E0B", label: "Away" };
  return { tone: "#94A3B8", label: "Offline" };
};

export const ParticipantsCard = ({ participants = [], onViewAll }) => {
  const active = participants.length;
  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-participants">
      <SectionTitle
        action={<span className="text-[10.5px] font-bold uppercase tracking-[0.14em]" style={{ color: "#6366F1" }}>{active} active</span>}
      >
        Participants
      </SectionTitle>
      {participants.length === 0 ? (
        <p className="text-[11.5px] py-3 text-center" style={{ color: LIGHT.textSubtle }}>No participants yet.</p>
      ) : (
        <ul className="space-y-2.5">
          {participants.slice(0, 5).map((p, i) => {
            const pres = presence(p.last_seen_at);
            return (
              <li key={p.user_id || p.id || i} className="flex items-center gap-2.5">
                <span
                  className="relative w-9 h-9 rounded-full text-white text-[10.5px] font-bold flex items-center justify-center shrink-0"
                  style={{ background: `linear-gradient(135deg, hsl(${(i * 67) % 360},48%,55%), hsl(${(i * 67 + 50) % 360},48%,38%))` }}
                >
                  {initialsOf(p.full_name || p.email)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[12.5px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>{p.full_name || p.email}</p>
                    {p.role && /you|self|me/i.test(p.role) && (
                      <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-1 h-3.5 rounded" style={{ background: "#EEF2FF", color: "#6366F1" }}>You</span>
                    )}
                  </div>
                  <p className="text-[10.5px] truncate" style={{ color: LIGHT.textSubtle }}>{p.role_label || p.role || "Participant"}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-[10.5px] shrink-0" style={{ color: LIGHT.textMuted }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: pres.tone }} /> {pres.label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {participants.length > 5 && (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${LIGHT.border}` }}>
          <SmallLink testid="dealroom-view-all-participants" onClick={onViewAll}>View all participants</SmallLink>
        </div>
      )}
    </section>
  );
};

/* ─── Awaiting card — operational blockers across all sources ─── */
const AWAITING_DOC_TOKENS = [
  { id: "ta6",       label: "TA6 (Property Information)",  tokens: ["ta6", "property information"] },
  { id: "ta10",      label: "TA10 (Fittings & Contents)",  tokens: ["ta10", "fittings"] },
  { id: "memo",      label: "Memorandum of Sale",          tokens: ["memorandum", "memo of sale"] },
  { id: "pof",       label: "Proof of Funds",              tokens: ["proof of funds", "pof"] },
  { id: "id",        label: "ID Verification",             tokens: ["id verification", "passport"] },
  { id: "mortgage",  label: "Mortgage Offer",              tokens: ["mortgage offer"] },
  { id: "leasehold", label: "Leasehold / Management Pack", tokens: ["leasehold", "management pack", "lpe1"] },
  { id: "searches",  label: "Searches",                    tokens: ["searches"] },
  { id: "contracts", label: "Draft Contract",              tokens: ["contract", "draft contract"] },
];

const AWAITING_ICONS = {
  ta6:       FileText,
  ta10:      FileText,
  memo:      FileText,
  pof:       FileText,
  id:        FileText,
  mortgage:  FileText,
  leasehold: FileText,
  searches:  Scale,
  contracts: FileText,
  default:   AlertCircle,
  task:      CheckCircle2,
};

const docMatched = (name, tokens) => {
  const n = (name || "").toLowerCase();
  return tokens.some((t) => n.includes(t));
};

const stageRequiresDoc = (deal, id) => {
  const s = (deal.stage || "").toLowerCase();
  const required = {
    ta6:       ["memo", "solicitors", "searches", "mortgage_app", "survey", "enquiries", "mortgage_offer", "exchange_ready"],
    ta10:      ["memo", "solicitors", "searches", "mortgage_app", "survey", "enquiries", "mortgage_offer", "exchange_ready"],
    memo:      ["solicitors", "searches", "mortgage_app", "survey", "enquiries", "mortgage_offer", "exchange_ready"],
    pof:       ["aml", "memo", "solicitors", "searches", "mortgage_app"],
    id:        ["aml", "memo", "solicitors"],
    mortgage:  ["mortgage_app", "survey", "enquiries", "exchange_ready"],
    leasehold: ["solicitors", "searches", "mortgage_app", "enquiries", "exchange_ready"],
    searches:  ["mortgage_app", "survey", "enquiries", "exchange_ready"],
    contracts: ["enquiries", "mortgage_offer", "exchange_ready"],
  };
  return (required[id] || []).includes(s);
};

const _t = (a) => (typeof a === "number" ? a : a ? Date.parse(a) : Date.now());

const PRIORITY_META = {
  high:   { bg: "#FEE2E2", fg: "#B91C1C", border: "#FECACA", label: "High" },
  medium: { bg: "#FEF3C7", fg: "#A16207", border: "#FDE68A", label: "Medium" },
  low:    { bg: "#DCFCE7", fg: "#15803D", border: "#BBF7D0", label: "Low" },
};

const ACTION_LABELS = { chase: "Chase", reminder: "Reminder", follow_up: "Follow up", resolve: "Resolve" };

/* Category buckets — operational groupings per product spec */
const GROUP_META = {
  urgent:        { label: "Urgent",            order: 0 },
  overdue:       { label: "Overdue",           order: 1 },
  pending_docs:  { label: "Pending documents", order: 2 },
  awaiting_resp: { label: "Awaiting response", order: 3 },
};

const classifyGroup = (it) => {
  if (it.daysSuffix === "Overdue") return "overdue";
  if (it.priority === "high" && it.source !== "document") return "urgent";
  if (it.source === "document") return "pending_docs";
  return "awaiting_resp";
};

const SUGGESTED = {
  overdue:       "Send a chase now to keep the chain moving.",
  urgent:        "Escalate or chase — exchange impact likely.",
  pending_docs:  "Request the document or send a polite reminder.",
  awaiting_resp: "Light follow-up usually unblocks within 24h.",
};

export const AwaitingCard = ({ deal, tasks = [], documents = [], recentlyChasedIds = {}, onAction, onMarkResolved, onChase, onEscalate, onAddUpdate, onRequestDoc, onReassign }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(null);

  const items = React.useMemo(() => {
    const list = [];

    // 1) Open tasks
    (tasks || [])
      .filter((t) => t.status !== "done")
      .forEach((t) => {
        const dueDays = t.due_at ? Math.round((Date.parse(t.due_at) - Date.now()) / 86_400_000) : null;
        const overdue = dueDays !== null && dueDays < 0;
        const dueToday = dueDays === 0;
        const daysWaiting = (() => {
          const created = _t(t.created_at);
          return Math.max(0, Math.floor((Date.now() - created) / 86_400_000));
        })();
        list.push({
          id: `task-${t.id}`,
          source: "task",
          docId: "task",
          taskId: t.id,
          title: t.title,
          subtitle: `Awaiting from ${t.owner_label || t.assignee_name || "team"}`,
          owner: t.owner_label || t.assignee_name || "Unassigned",
          ownerSub: t.owner_role_label || "",
          priority: overdue ? "high" : dueToday ? "high" : (dueDays !== null && dueDays <= 2) ? "medium" : "low",
          days: overdue ? Math.abs(dueDays) : (dueDays === null ? daysWaiting : dueDays),
          daysSuffix: overdue ? "Overdue" : (dueDays === null ? "Waiting" : "Remaining"),
          action: overdue ? "chase" : dueToday ? "reminder" : "follow_up",
          sortKey: overdue ? -100000 + dueDays : dueDays !== null ? dueDays : 50,
        });
      });

    // 2) Required documents missing for current stage
    AWAITING_DOC_TOKENS.forEach((d) => {
      const have = (documents || []).some((doc) => docMatched(doc.name, d.tokens));
      if (!have && stageRequiresDoc(deal, d.id)) {
        const [owner, ownerSub] = (() => {
          if (["mortgage", "pof", "id"].includes(d.id))   return ["Buyer / Broker",   "Buyer side"];
          if (["leasehold", "ta6", "ta10"].includes(d.id)) return ["Seller Solicitor", "Seller side"];
          if (["searches", "contracts"].includes(d.id))    return ["Buyer Solicitor",  "Buyer side"];
          return ["Solicitor", ""];
        })();
        list.push({
          id: `doc-${d.id}`,
          source: "document",
          docId: d.id,
          title: d.label.replace(/\s*\(.*\)$/, ""),
          subtitle: `Awaiting from ${owner.toLowerCase()}`,
          owner,
          ownerSub,
          priority: "medium",
          days: 5,
          daysSuffix: "Remaining",
          action: "reminder",
          sortKey: 10,
        });
      }
    });

    // 3) Risk-keyword activities
    (deal.activity || []).forEach((a, idx) => {
      const at = _t(a.at);
      const daysWaiting = Math.floor((Date.now() - at) / 86_400_000);
      const text = (a.text || a.kind || "").toLowerCase();
      if (daysWaiting > 30) return;
      if (/awaiting|blocked|delay|risk|missing/.test(text)) {
        list.push({
          id: `act-${a.id || idx}-${at}`,
          source: "activity",
          docId: "default",
          title: a.text || a.kind || "Operational blocker",
          subtitle: `Reported by ${a.actor || "system"}`,
          owner: a.actor || "—",
          ownerSub: "",
          priority: daysWaiting > 5 ? "high" : daysWaiting > 2 ? "medium" : "low",
          days: daysWaiting,
          daysSuffix: daysWaiting > 0 ? "waiting" : "Today",
          action: "chase",
          sortKey: -daysWaiting,
        });
      }
    });

    return list.sort((a, b) => a.sortKey - b.sortKey);
  }, [deal, tasks, documents]);

  const total = items.length;

  /* Group items by category */
  const groups = React.useMemo(() => {
    const map = { urgent: [], overdue: [], pending_docs: [], awaiting_resp: [] };
    items.forEach((it) => {
      it._group = classifyGroup(it);
      map[it._group].push(it);
    });
    return Object.entries(map)
      .filter(([, arr]) => arr.length > 0)
      .sort(([a], [b]) => GROUP_META[a].order - GROUP_META[b].order)
      .map(([id, arr]) => ({ id, label: GROUP_META[id].label, items: arr }));
  }, [items]);

  const visibleSlice = (arr, idx) => (expanded ? arr : arr.slice(0, idx));

  const handlePrimary = (it) => {
    if (it.source === "task") onMarkResolved?.(it);
    else if (it.action === "chase") onChase?.(it);
    else onAction?.(it);
  };

  return (
    <section className="rounded-2xl p-5 sm:p-6" style={cardStyle} data-testid="dealroom-awaiting">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[15px] font-bold" style={{ color: LIGHT.textPrimary }}>
          Awaiting <span className="font-semibold" style={{ color: LIGHT.textMuted }}>({total})</span>
        </h3>
        {total > 5 && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="text-[11.5px] font-semibold inline-flex items-center gap-1"
            style={{ color: "#6366F1" }}
            data-testid="dealroom-awaiting-view-all"
          >
            {expanded ? "Show fewer" : `View all (${total})`}
            <ChevronRight size={11} className={expanded ? "-rotate-90" : "rotate-90"} />
          </button>
        )}
      </div>

      {total === 0 ? (
        <p className="text-[12px] py-6 text-center" style={{ color: LIGHT.textSubtle }}>
          Nothing waiting — chain is clear.
        </p>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => {
            const cap = expanded ? g.items.length : (g.id === "overdue" || g.id === "urgent" ? 5 : 3);
            const slice = g.items.slice(0, cap);
            const remain = g.items.length - slice.length;
            return (
              <div key={g.id} data-testid={`dealroom-awaiting-group-${g.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em]"
                    style={{ color: g.id === "overdue" ? "#B91C1C" : g.id === "urgent" ? "#A16207" : LIGHT.textSubtle }}
                  >
                    {g.label}
                  </span>
                  <span
                    className="text-[10px] font-bold tabular-nums px-1.5 h-[16px] inline-flex items-center rounded-full"
                    style={{
                      background: g.id === "overdue" ? "#FEE2E2" : g.id === "urgent" ? "#FEF3C7" : "#F1F5F9",
                      color: g.id === "overdue" ? "#B91C1C" : g.id === "urgent" ? "#A16207" : LIGHT.textMuted,
                    }}
                  >
                    {g.items.length}
                  </span>
                  <span
                    className="text-[10.5px] truncate ml-2 hidden md:inline"
                    style={{ color: LIGHT.textSubtle }}
                  >
                    {SUGGESTED[g.id]}
                  </span>
                </div>
                <ul className="divide-y" style={{ borderColor: LIGHT.border }}>
                  {slice.map((it) => (
                    <AwaitingRow
                      key={it.id}
                      item={it}
                      sentRecently={!!recentlyChasedIds[it.id]}
                      menuOpen={openMenu === it.id}
                      onMenuToggle={() => setOpenMenu(openMenu === it.id ? null : it.id)}
                      onMenuClose={() => setOpenMenu(null)}
                      onPrimary={() => handlePrimary(it)}
                      onChase={() => { setOpenMenu(null); onChase?.(it); }}
                      onEscalate={() => { setOpenMenu(null); onEscalate?.(it); }}
                      onMarkComplete={() => { setOpenMenu(null); onMarkResolved?.(it); }}
                      onReassign={() => { setOpenMenu(null); onReassign?.(it); }}
                      onAddUpdate={() => { setOpenMenu(null); onAddUpdate?.(it); }}
                      onRequestDoc={() => { setOpenMenu(null); onRequestDoc?.(it); }}
                    />
                  ))}
                </ul>
                {remain > 0 && !expanded && (
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="mt-1 text-[10.5px] font-semibold"
                    style={{ color: LIGHT.textMuted }}
                    data-testid={`dealroom-awaiting-group-more-${g.id}`}
                  >
                    Show {remain} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

/* ─── Single awaiting row + inline action menu ─────────────── */
const AwaitingRow = ({
  item: it,
  sentRecently,
  menuOpen, onMenuToggle, onMenuClose,
  onPrimary,
  onChase, onEscalate, onMarkComplete, onReassign, onAddUpdate, onRequestDoc,
}) => {
  const p = PRIORITY_META[it.priority];
  const isOverdue = it.daysSuffix === "Overdue";
  const Icon = AWAITING_ICONS[it.docId] || AWAITING_ICONS.default;
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) onMenuClose(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen, onMenuClose]);

  return (
    <li
      className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] gap-3 sm:gap-4 items-center py-3.5"
      data-testid={`dealroom-awaiting-item-${it.id}`}
    >
      <span
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "#FEF3C7", color: "#A16207" }}
      >
        <Icon size={14} strokeWidth={2.2} />
      </span>
      <div className="min-w-0">
        <p className="text-[13px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>
          {it.title}
        </p>
        <p className="text-[11px] mt-0.5 truncate" style={{ color: LIGHT.textSubtle }}>
          {it.subtitle}
        </p>
      </div>
      <div className="hidden md:flex flex-col items-start text-left min-w-[120px]">
        <span className="text-[12px] font-semibold" style={{ color: LIGHT.textPrimary }}>{it.owner}</span>
        {it.ownerSub && <span className="text-[10.5px]" style={{ color: LIGHT.textSubtle }}>{it.ownerSub}</span>}
      </div>
      <div className="hidden sm:flex flex-col items-start text-left min-w-[80px]">
        <span
          className="text-[12.5px] font-bold tabular-nums"
          style={{ color: isOverdue ? "#DC2626" : LIGHT.textPrimary }}
        >
          {it.days} {it.days === 1 ? "day" : "days"}
        </span>
        <span
          className="text-[10.5px]"
          style={{ color: isOverdue ? "#DC2626" : LIGHT.textSubtle }}
        >
          {it.daysSuffix}
        </span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 relative">
        <span
          className="hidden sm:inline-flex items-center h-[24px] px-2 rounded-md text-[10.5px] font-bold uppercase tracking-[0.10em]"
          style={{ background: p.bg, color: p.fg, border: `1px solid ${p.border}` }}
        >
          {p.label}
        </span>
        <button
          type="button"
          onClick={sentRecently ? undefined : onPrimary}
          disabled={sentRecently}
          className="h-[28px] px-3 rounded-md text-[11px] font-semibold transition-colors hover:bg-slate-100 disabled:cursor-default"
          style={
            sentRecently
              ? { background: "#DCFCE7", border: "1px solid #BBF7D0", color: "#15803D" }
              : { background: "#FFFFFF", border: `1px solid ${LIGHT.borderStrong}`, color: LIGHT.textPrimary }
          }
          data-testid={`dealroom-awaiting-action-${it.id}`}
        >
          {sentRecently ? "Sent ✓" : (ACTION_LABELS[it.action] || "Action")}
        </button>
        <button
          type="button"
          onClick={onMenuToggle}
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="w-7 h-[28px] rounded-md inline-flex items-center justify-center transition-colors hover:bg-slate-100"
          style={{ background: "transparent", color: LIGHT.textMuted }}
          data-testid={`dealroom-awaiting-menu-${it.id}`}
        >
          <MoreHorizontal size={14} />
        </button>
        {menuOpen && (
          <div
            ref={menuRef}
            role="menu"
            className="absolute top-full right-0 mt-1.5 w-[180px] rounded-lg overflow-hidden z-30"
            style={{
              background: "#FFFFFF",
              border: `1px solid ${LIGHT.borderStrong}`,
              boxShadow: "0 16px 36px -12px rgba(15,23,42,0.18)",
            }}
            data-testid={`dealroom-awaiting-menu-popover-${it.id}`}
          >
            <MenuItem icon={Send}        label="Chase"            onClick={onChase}        testid={`menu-chase-${it.id}`} />
            <MenuItem icon={AlertCircle} label="Escalate"         onClick={onEscalate}     testid={`menu-escalate-${it.id}`} tone="warn" />
            <MenuItem icon={CheckCircle2} label="Mark complete"   onClick={onMarkComplete} testid={`menu-complete-${it.id}`} />
            <MenuItem icon={Users}       label="Reassign"         onClick={onReassign}     testid={`menu-reassign-${it.id}`} />
            <MenuItem icon={Plus}        label="Add update"       onClick={onAddUpdate}    testid={`menu-update-${it.id}`} />
            <MenuItem icon={FileText}    label="Request document" onClick={onRequestDoc}   testid={`menu-request-${it.id}`} />
          </div>
        )}
      </div>
    </li>
  );
};

const MenuItem = ({ icon: I, label, onClick, testid, tone }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className="w-full flex items-center gap-2.5 h-9 px-3 text-left text-[12px] font-semibold transition-colors hover:bg-slate-50"
    style={{ color: tone === "warn" ? "#A16207" : LIGHT.textPrimary }}
    data-testid={testid}
  >
    <I size={12} />
    {label}
  </button>
);


/* ─── Next Key Date card ────────────────────────────────────────── */
export const NextKeyDateCard = ({ deal }) => {
  const target = deal.targetExchange;
  const days = daysFromNow(target);
  if (!target) return null;
  const dt = new Date(Date.parse(target));
  const day = String(dt.getDate());
  const month = dt.toLocaleString("en-GB", { month: "short" }).toUpperCase();
  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-next-key-date">
      <h3 className="text-[13.5px] font-bold mb-3" style={{ color: LIGHT.textPrimary }}>
        Next Key Date
      </h3>
      <div className="flex items-center gap-3">
        <div
          className="rounded-lg flex flex-col items-center justify-center shrink-0 leading-none"
          style={{
            width: 46, height: 50,
            background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(99,102,241,0.05))",
            border: "1px solid rgba(99,102,241,0.18)",
            color: "#6366F1",
          }}
        >
          <span className="text-[16px] font-bold tabular-nums">{day}</span>
          <span className="text-[8.5px] font-bold tracking-[0.14em] mt-1">{month}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold" style={{ color: LIGHT.textPrimary }}>Exchange Target</p>
          <p className="text-[12px] font-bold mt-0.5" style={{ color: days !== null && days < 0 ? "#DC2626" : "#6366F1" }}>
            {days === null ? fmtDate(target) : days < 0 ? `${Math.abs(days)} days overdue` : `In ${days} days`}
          </p>
        </div>
      </div>
      <button
        type="button"
        className="mt-3 w-full h-8 rounded-md text-[11.5px] font-semibold"
        style={{ background: "transparent", color: LIGHT.textMuted, border: `1px solid ${LIGHT.border}` }}
        data-testid="dealroom-next-key-date-view"
      >
        View all dates
      </button>
    </section>
  );
};

/* ─── Deal Health Summary (structured fields, matches reference) ─ */
export const DealHealthSummary = ({ deal, lastActivityTs }) => {
  const ch = deal.chainHealth ?? 90;
  const riskLevel = ch >= 80 ? { label: "Low",    fg: "#15803D", dot: "#10B981" }
                  : ch >= 60 ? { label: "Medium", fg: "#A16207", dot: "#F59E0B" }
                             : { label: "High",   fg: "#B91C1C", dot: "#EF4444" };
  const lastAct = lastActivityTs
    ? (() => {
        const m = Math.floor((Date.now() - lastActivityTs) / 60000);
        if (m < 60) return `${m} mins ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h} hours ago`;
        return `${Math.floor(h / 24)} days ago`;
      })()
    : "—";
  const updatedTime = (() => {
    const d = new Date();
    return `Updated today at ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  })();

  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-deal-health-summary">
      <div className="flex items-center gap-2 mb-1">
        <span
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: "rgba(16,185,129,0.10)", color: "#10B981", border: "1px solid rgba(16,185,129,0.22)" }}
        >
          <Sparkles size={11} />
        </span>
        <h3 className="text-[13.5px] font-bold tracking-tight" style={{ color: LIGHT.textPrimary }}>
          Deal Health
        </h3>
      </div>
      <p className="text-[10.5px] mb-3" style={{ color: LIGHT.textSubtle }}>{updatedTime}</p>
      <dl className="space-y-2">
        <SummaryRow
          label="Risk Level"
          value={
            <span className="inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: riskLevel.fg }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: riskLevel.dot }} />
              {riskLevel.label}
            </span>
          }
        />
        <SummaryRow label="Exchange Target" value={fmtDate(deal.targetExchange)} />
        <SummaryRow label="Chain Position"  value={deal.chainPosition || "Middle"} />
        <SummaryRow label="Last Activity"   value={lastAct} />
      </dl>
    </section>
  );
};

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="text-[12px]" style={{ color: LIGHT.textMuted }}>{label}</dt>
    <dd className="text-[12px] font-bold tabular-nums text-right" style={{ color: LIGHT.textPrimary }}>{value}</dd>
  </div>
);

/* ─── Outstanding Actions card (legacy alias kept for compatibility) ─ */
export const OutstandingActionsCard = ({ tasks = [], onAll }) => {
  const open = tasks.filter((t) => t.status !== "done").slice(0, 3);
  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-outstanding-actions">
      <SectionTitle
        action={
          <span className="text-[10.5px] font-bold inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: "#FEE2E2", color: "#DC2626" }}>
            {tasks.filter((t) => t.status !== "done").length}
          </span>
        }
      >
        Outstanding Actions
      </SectionTitle>
      {open.length === 0 ? (
        <p className="text-[11.5px] py-3 text-center" style={{ color: LIGHT.textSubtle }}>All actions cleared.</p>
      ) : (
        <ul className="space-y-2.5">
          {open.map((t) => {
            const dueDays = t.due_at ? Math.round((Date.parse(t.due_at) - Date.now()) / 86_400_000) : null;
            const overdue = dueDays !== null && dueDays < 0;
            const dueToday = dueDays === 0;
            const tone = overdue ? "#DC2626" : dueToday ? "#D97706" : "#64748B";
            const label = overdue ? `Overdue` : dueToday ? "Due Today" : dueDays !== null ? `Due in ${dueDays} days` : "No due date";
            return (
              <li key={t.id} className="flex items-start gap-2.5" data-testid={`dealroom-outstanding-${t.id}`}>
                <span
                  className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: "#FEF3C7", color: "#D97706" }}
                >
                  <FileText size={11} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>{t.title}</p>
                  <p className="text-[10.5px] mt-0.5 truncate" style={{ color: LIGHT.textSubtle }}>
                    {t.description || (t.due_at ? fmtDate(t.due_at) : "No metadata")}
                  </p>
                </div>
                <span className="text-[10.5px] font-bold tabular-nums shrink-0" style={{ color: tone }}>{label}</span>
              </li>
            );
          })}
        </ul>
      )}
      {tasks.length > 3 && (
        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${LIGHT.border}` }}>
          <SmallLink testid="dealroom-view-all-actions" onClick={onAll}>
            View all actions ({tasks.length})
          </SmallLink>
        </div>
      )}
    </section>
  );
};

/* ─── Exchange Readiness checklist ─────────────────────────────── */
const READINESS_TOKENS = [
  { id: "aml",        label: "AML Checks",       tokens: ["aml"] },
  { id: "searches",   label: "Searches Ordered", tokens: ["searches"] },
  { id: "mortgage",   label: "Mortgage Offer",   tokens: ["mortgage offer", "mortgage_offer"] },
  { id: "enquiries",  label: "Enquiries Raised", tokens: ["enquiries"] },
  { id: "contracts",  label: "Contracts Issued", tokens: ["contract", "draft contract"] },
  { id: "deposit",    label: "Deposit Received", tokens: ["deposit"] },
  { id: "exchange",   label: "Exchange Ready",   tokens: ["exchange ready", "exchange_ready"] },
  { id: "completion", label: "Completion Ready", tokens: ["completion"] },
];

export const ExchangeReadinessCard = ({ deal, documents = [] }) => {
  const items = READINESS_TOKENS.map((it) => {
    const matched = documents.some((d) => {
      const n = (d.name || "").toLowerCase();
      return it.tokens.some((t) => n.includes(t));
    });
    const stageMatched = (() => {
      const s = (deal.stage || "").toLowerCase();
      if (it.id === "aml" && ["aml", "memo", "solicitors", "searches", "mortgage_app", "survey", "enquiries", "mortgage_offer", "exchange_ready", "exchanged", "completed"].includes(s)) return true;
      if (it.id === "searches" && ["searches", "mortgage_app", "survey", "enquiries", "mortgage_offer", "exchange_ready", "exchanged", "completed"].includes(s)) return true;
      if (it.id === "mortgage" && ["mortgage_offer", "exchange_ready", "exchanged", "completed"].includes(s)) return true;
      if (it.id === "enquiries" && ["enquiries", "mortgage_offer", "exchange_ready", "exchanged", "completed"].includes(s)) return true;
      if (it.id === "exchange" && ["exchanged", "completed"].includes(s)) return true;
      if (it.id === "completion" && ["completed"].includes(s)) return true;
      return false;
    })();
    return { ...it, done: matched || stageMatched };
  });
  const completed = items.filter((i) => i.done).length;
  const total = items.length;
  const pct = (completed / total) * 100;

  return (
    <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-exchange-readiness">
      <SectionTitle>Exchange Readiness</SectionTitle>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[14px] font-bold tabular-nums" style={{ color: LIGHT.textPrimary }}>
          {completed} / {total} <span className="text-[12px]" style={{ color: LIGHT.textMuted }}>Complete</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full mb-4" style={{ background: "#F1F5F9" }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg, #10B981, #22C55E)" }} />
      </div>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it.id} className="flex items-center gap-2.5" data-testid={`dealroom-readiness-${it.id}`}>
            {it.done ? (
              <CheckCircle2 size={14} className="shrink-0" style={{ color: "#10B981" }} strokeWidth={2.4} />
            ) : (
              <ChevronRight size={14} className="shrink-0" style={{ color: LIGHT.textSubtle }} />
            )}
            <span className="flex-1 text-[12px] font-semibold" style={{ color: it.done ? LIGHT.textPrimary : LIGHT.textMuted }}>
              {it.label}
            </span>
            <span
              className="w-4 h-4 rounded-full inline-flex items-center justify-center shrink-0"
              style={{
                background: it.done ? "#DCFCE7" : "transparent",
                border: it.done ? "1px solid #BBF7D0" : `1px solid ${LIGHT.border}`,
              }}
            >
              {it.done && <CheckCircle2 size={10} style={{ color: "#10B981" }} strokeWidth={3} />}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

/* ─── AI Insight (DARK) card ───────────────────────────────────── */
export const AIInsightCard = ({ deal, onView }) => {
  const ch = deal.chainHealth ?? 90;
  const probDelta = ch >= 80 ? "increased by 6%" : ch >= 65 ? "decreased by 8%" : "decreased by 14%";
  const reason = ch >= 80
    ? "all parties responsive, documents up to date."
    : ch >= 65
      ? "solicitor responsiveness slipping over the past 48 hours."
      : "solicitor inactivity and missing documents.";

  return (
    <section
      className="relative rounded-2xl p-5 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #1A1635 0%, #14122B 100%)",
        border: "1px solid rgba(139,92,246,0.35)",
        boxShadow: "0 16px 36px -16px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
      data-testid="dealroom-ai-insight"
    >
      {/* Inner glow */}
      <span aria-hidden="true" className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(139,92,246,0.45), transparent 70%)" }} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-1.5">
            <span className="w-6 h-6 rounded-md inline-flex items-center justify-center" style={{ background: "rgba(139,92,246,0.22)", color: "#C4B5FD" }}>
              <Sparkles size={11} />
            </span>
            <span className="font-display text-[13px] font-bold text-white tracking-tight">Insight</span>
          </div>
          <span className="text-[8.5px] font-bold uppercase tracking-[0.18em] px-1.5 h-4 inline-flex items-center rounded"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "#94a3b8" }}
          >
            BETA
          </span>
        </div>
        <p className="text-[12.5px] text-slate-200 leading-relaxed mb-4">
          Exchange probability has <span className="font-bold text-white">{probDelta}</span> based on {reason}
        </p>
        <button
          type="button"
          onClick={onView}
          data-testid="dealroom-ai-view-insight"
          className="w-full h-9 rounded-lg inline-flex items-center justify-between px-3 text-white text-[12px] font-bold transition-transform hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
            boxShadow: "0 8px 22px -8px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
          }}
        >
          View insight <ArrowRight size={12} />
        </button>
      </div>
    </section>
  );
};

/* ─── Quick Actions ─────────────────────────────────────────────── */
import { Phone, KeyRound, Bell, UploadCloud, CalendarCheck, MessageSquarePlus, BadgeCheck } from "lucide-react";

const QUICK_ACTIONS = [
  { id: "chase",          label: "Chase Solicitor",     Icon: Users,             tone: "rose" },
  { id: "request",        label: "Request Document",    Icon: FileText,          tone: "indigo" },
  { id: "reminder",       label: "Send Reminder",       Icon: Bell,              tone: "amber" },
  { id: "upload",         label: "Upload Contract",     Icon: UploadCloud,       tone: "indigo" },
  { id: "confirm-target", label: "Confirm Target",      Icon: CalendarCheck,     tone: "emerald" },
  { id: "schedule-call",  label: "Schedule Call",       Icon: Phone,             tone: "indigo" },
  { id: "notify-buyer",   label: "Notify Buyer",        Icon: MessageSquarePlus, tone: "indigo" },
  { id: "request-id",     label: "Request ID Verify",   Icon: BadgeCheck,        tone: "indigo" },
  { id: "send-memo",      label: "Send Memorandum",     Icon: KeyRound,          tone: "indigo" },
];

const QA_TONE = {
  rose:    { bg: "#FFF1F2", fg: "#E11D48" },
  amber:   { bg: "#FFFBEB", fg: "#D97706" },
  emerald: { bg: "#ECFDF5", fg: "#059669" },
  indigo:  { bg: "#EEF2FF", fg: "#6366F1" },
};

export const QuickActionsCard = ({ onAction }) => (
  <section className="rounded-2xl p-4" style={cardStyle} data-testid="dealroom-quick-actions">
    <SectionTitle>Quick Actions</SectionTitle>
    <div className="grid grid-cols-3 gap-2">
      {QUICK_ACTIONS.map(({ id, label, Icon, tone }) => {
        const t = QA_TONE[tone] || QA_TONE.indigo;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onAction?.(id)}
            data-testid={`dealroom-qa-${id}`}
            className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-sm text-center"
            style={{ border: `1px solid ${LIGHT.border}`, background: "#FFFFFF" }}
          >
            <span
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: t.bg, color: t.fg }}
            >
              <Icon size={14} />
            </span>
            <span className="text-[10px] font-semibold leading-tight" style={{ color: LIGHT.textPrimary }}>{label}</span>
          </button>
        );
      })}
    </div>
  </section>
);
